import type { CredentialProfileDetail } from "@aiyo/shared";
import { Pencil, TrashBin } from "@gravity-ui/icons";
import { Button, Chip, toast } from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { ConfirmDialog, FormDialog } from "../components/dialogs";
import { TextAreaInput, TextInput } from "../components/fields";
import { ProfileEntries } from "../components/ProfileEntries";
import { Callout, DangerZone, ErrorBlock, LoadingBlock, Page, PageHeader, Panel } from "../components/ui";
import { useDeleteProfile, useMe, useProfile, useUpdateCredentialProfile } from "../lib/queries";

export function CredentialProfilePage() {
  const { profileId = "" } = useParams();
  const profile = useProfile(profileId);
  if (profile.isPending) return <LoadingBlock />;
  if (profile.isError) return <ErrorBlock error={profile.error} onRetry={() => profile.refetch()} />;
  return <ProfileView profile={profile.data} />;
}

function ProfileView({ profile }: { profile: CredentialProfileDetail }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const canEdit = me?.role !== "viewer";
  const [editOpen, setEditOpen] = useState(false);

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <Link to="/credential-profiles" className="hover:text-foreground">
            {t("profiles.title")}
          </Link>
        }
        title={profile.name}
        description={profile.description ?? undefined}
        actions={
          canEdit ? (
            <Button variant="secondary" onPress={() => setEditOpen(true)}>
              <Pencil />
              {t("profiles.editTitle")}
            </Button>
          ) : null
        }
      />

      <Panel title={t("profiles.usedBy")}>
        {profile.apps.length ? (
          <div className="flex flex-wrap gap-2">
            {profile.apps.map((a) => (
              <Link
                key={a.id}
                to={`/apps/${a.id}?tab=credentials`}
                className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <Chip variant="secondary">{a.name}</Chip>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted">{t("profiles.noApps")}</p>
        )}
      </Panel>

      <Panel title={t("profiles.entries")} description={t("profiles.entriesHint")}>
        {profile.apps.length > 1 && canEdit ? (
          <Callout tone="warning">
            {t("profiles.shared", { apps: profile.apps.map((a) => a.name).join(", ") })}
          </Callout>
        ) : null}
        <ProfileEntries profile={profile} canEdit={canEdit} />
      </Panel>

      {canEdit ? <DeleteProfile profile={profile} /> : null}
      {canEdit ? <EditProfileDialog profile={profile} isOpen={editOpen} onOpenChange={setEditOpen} /> : null}
    </Page>
  );
}

function EditProfileDialog({
  profile,
  isOpen,
  onOpenChange,
}: {
  profile: CredentialProfileDetail;
  isOpen: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateCredentialProfile(profile.id);
  const [name, setName] = useState(profile.name);
  const [description, setDescription] = useState(profile.description ?? "");

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the dialog opens.
  useEffect(() => {
    if (isOpen) {
      setName(profile.name);
      setDescription(profile.description ?? "");
      update.reset();
    }
  }, [isOpen]);

  return (
    <FormDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("profiles.editTitle")}
      submitLabel={t("common.save")}
      isPending={update.isPending}
      isSubmitDisabled={!name.trim()}
      error={update.error}
      onSubmit={() =>
        update.mutate(
          { name: name.trim(), description: description.trim() || null },
          {
            onSuccess: () => {
              onOpenChange(false);
              toast.success(t("profiles.saved"));
            },
          },
        )
      }
    >
      <TextInput name="profile-name" label={t("profiles.name")} value={name} onChange={setName} isRequired />
      <TextAreaInput
        name="profile-description"
        label={`${t("profiles.description")} (${t("common.optional").toLowerCase()})`}
        placeholder={t("profiles.descriptionPlaceholder")}
        value={description}
        onChange={setDescription}
        rows={2}
      />
    </FormDialog>
  );
}

function DeleteProfile({ profile }: { profile: CredentialProfileDetail }) {
  const { t } = useTranslation();
  const remove = useDeleteProfile();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const inUse = profile.apps.length > 0;
  return (
    <DangerZone
      title={t("profiles.dangerZone")}
      description={inUse ? t("profiles.inUse") : t("profiles.deleteHint")}
    >
      <Button variant="danger-soft" isDisabled={inUse} onPress={() => setOpen(true)}>
        <TrashBin />
        {t("common.delete")}
      </Button>
      <ConfirmDialog
        isOpen={open}
        onOpenChange={setOpen}
        title={t("profiles.deleteTitle")}
        body={t("profiles.deleteBody")}
        confirmLabel={t("common.delete")}
        isPending={remove.isPending}
        error={remove.error}
        onConfirm={() =>
          remove.mutate(profile.id, {
            onSuccess: () => {
              setOpen(false);
              toast.success(t("profiles.deleted"));
              navigate("/credential-profiles", { replace: true });
            },
          })
        }
      />
    </DangerZone>
  );
}
